// ============================================
// useCumulativeDelta.js
// Running CVD candles aligned to price candles.
//
// CVD is a running sum of (buys - sells). For each candle:
//   open  = previous candle's close (running carry)
//   close = open + delta
//   high  = open + total_buys   (max CVD reachable; all buys before any sell)
//   low   = open - total_sells  (min CVD reachable; all sells before any buy)
//
// The bound is exact — CVD inside the candle cannot exit [low, high] no
// matter the trade order. Trade order within a single candle is micro-
// structure noise, so the bound is the meaningful OHLC representation.
// On a green bar both wicks = total sells (opposing flow); on red, = buys.
//
// Anchor: first candle with any flow. Pre-anchor candles (historical OHLC
// backfill from GeckoTerminal with no level data, plus quiet leading
// candles) are flat zero so the zero line reads as "session start," not
// "array's oldest entry." `startIndex` is exposed for the renderer to
// mark the anchor and to scale from it.
//
// IMPORTANT: this hook reads candle flow directly from `_liveLevels` /
// `_native` and skips the renderer's `_display` cache. `_display` lags
// by one render cycle on the live candle (drawCanvas rebuilds it in a
// useLayoutEffect that runs AFTER this useMemo commits), so going to
// the source guarantees CVD reflects the latest tick on the same frame.
//
// INCREMENTAL: the running sum through the CLOSED candles is immutable
// between structural changes (a closed candle's flow never changes), so
// it's cached and only the LIVE candle's bar is recomputed each tick.
// `structuralVersion` (from useChartData) bumps on close/trim/rebuild/
// switch — and on a late trade landing in a closed candle — which is
// exactly when the closed portion must be rebuilt. The live bar still
// updates every tick, so the subgraph tracks intra-bar flow unchanged.
//
// Return shape:
//   { bars: Map<startTime, { open, high, low, close, delta }>,
//     currentValue,
//     startIndex }
// ============================================
import { useMemo, useRef } from 'react'

function candleFlow(c) {
  // _omFootprint history (OpenMarket futures) carries real buy/sell levels —
  // let it contribute true delta; plain OHLCV history still reads as zero.
  if (!c || (c.isHistorical && !c._omFootprint)) return { buy: 0, sell: 0, delta: 0 }
  // Live or closed-but-unpacked: _liveLevels Map keyed by stepIdx.
  if (c._liveLevels) {
    let buy = 0, sell = 0
    for (const lvl of c._liveLevels.values()) {
      buy  += lvl.buy  || 0
      sell += lvl.sell || 0
    }
    return { buy, sell, delta: buy - sell }
  }
  // Packed: totals are pre-summed on the candle.
  if (c._native) {
    const totalVol = c._nativeTotalVol || 0
    const delta    = c._nativeDelta    || 0
    return { buy: (totalVol + delta) / 2, sell: (totalVol - delta) / 2, delta }
  }
  return { buy: 0, sell: 0, delta: 0 }
}

// Pure incremental core (no React) — testable in isolation. Mutates `cache`
// ({ ver, bars, closedLen, runningThroughClosed, closedStartIndex }) and
// returns { bars, currentValue, startIndex }. Output is identical to a full
// single-pass rebuild; see the equivalence test.
export function cvdCompute(candles, structuralVersion, cache, enabled = true) {
  if (!enabled || !candles || candles.length === 0) {
    cache.ver = -1; cache.bars = null; cache.closedLen = -1
    cache.runningThroughClosed = 0; cache.closedStartIndex = -1
    return { bars: new Map(), currentValue: 0, startIndex: -1 }
  }

  const len = candles.length
  const structural = cache.ver !== structuralVersion || !cache.bars || cache.closedLen !== len - 1

  if (structural) {
    // Rebuild the CLOSED portion [0 .. len-2]. Immutable until the next
    // structural change, so the running total through it is cached.
    const bars = new Map()
    let running = 0
    let startIndex = -1
    for (let i = 0; i < len - 1; i++) {
      const c = candles[i]
      const { buy, sell, delta } = candleFlow(c)
      if (startIndex === -1 && (buy > 0 || sell > 0)) startIndex = i
      const open = running
      bars.set(c.startTime, { open, high: open + buy, low: open - sell, close: open + delta, delta })
      running = open + delta
    }
    cache.ver = structuralVersion
    cache.bars = bars
    cache.closedLen = len - 1
    cache.runningThroughClosed = running
    cache.closedStartIndex = startIndex
  }

  // Live bar (index len-1) — recomputed every tick from the stable closed
  // total, so it tracks intra-bar flow at O(1).
  const bars = cache.bars
  const liveIdx = len - 1
  const live = candles[liveIdx]
  const { buy, sell, delta } = candleFlow(live)
  const open = cache.runningThroughClosed
  const close = open + delta
  bars.set(live.startTime, { open, high: open + buy, low: open - sell, close, delta })
  const startIndex = cache.closedStartIndex >= 0
    ? cache.closedStartIndex
    : ((buy > 0 || sell > 0) ? liveIdx : -1)

  return { bars, currentValue: close, startIndex }
}

export default function useCumulativeDelta(candles, { enabled = true, structuralVersion = 0 } = {}) {
  const cacheRef = useRef({ ver: -1, bars: null, closedLen: -1, runningThroughClosed: 0, closedStartIndex: -1 })
  return useMemo(
    () => cvdCompute(candles, structuralVersion, cacheRef.current, enabled),
    [candles, enabled, structuralVersion],
  )
}
